// ════════════════════════════════════════════════════════════════════════════
// The attach-result surface's fixed strings and ceilings.
//
// Two block grammars meet here and they are NOT symmetric:
//
//   ``` attach-result ```      what a MODEL writes. Read once, at the write
//                              seam, and never persisted. Its fixed strings
//                              are below.
//   ``` no-edit-data-block ``` what WE write in its place. Persisted forever,
//                              re-read by models, guarded against edits. Its
//                              printed grammar — fence, bullet, indent,
//                              directives, ceilings — lives in `grammar.ts`;
//                              what stays here is the CAPTION, which U11 ruled
//                              verbatim and which the grammar does not govern.
//
// Ruling authority: FOLLOWUP-URL-SURFACE.md §7 (U11, U14, U16), DELEGATION-
// BUILD.md §2.1, and the 2026-08-19 format ruling (planning/attach-format/
// SPEC.md) that replaced the persisted block's JSON body with the authored
// grammar and deleted its START/END markers.
// ════════════════════════════════════════════════════════════════════════════

/** Envelope version. A literal, not a range: a reader that meets a different
 *  one is not looking at this contract and must not try to read it as one. */
export const ATTACHED_RESULT_SCHEMA_VERSION = 1;

// ─── The authoring fence (model-written) ────────────────────────────────────

/** Info string of the block a model writes to attach results. Compared after
 *  `trim().toLowerCase()`, the same normalization the render side applies. */
export const ATTACH_FENCE_INFO_STRING = 'attach-result';

/** Params are `key: value`, one per line — the same directive shape
 *  `parse-map-block.ts` reads, so a model that has written one block has
 *  written both. The key set is closed (see `AttachBlockParamKey`); an
 *  unrecognized key is counted and dropped, never guessed at. */
export const ATTACH_PARAM_REGEX = /^([a-z-]+)\s*:(.*)$/i;

/** One optional markdown bullet opening a line. Models list links as bullets
 *  by reflex, and a bullet is decoration around the item, not part of it. */
export const ATTACH_BULLET_REGEX = /^[-*+](?:\s+|$)/;

/** A markdown link item — `[label](url)`, ruling U14. The label may be empty
 *  and may contain anything but a bracket; the URL runs to the first closing
 *  paren, which is what every markdown renderer a model has seen also does. */
export const ATTACH_MARKDOWN_LINK_REGEX = /\[([^\]]*)\]\(\s*([^)\s]+)\s*\)/g;

/** A bare http(s) URL. Read INSIDE the links value only — a model that wrote
 *  the address without its markdown wrapper still named a result, and dropping
 *  it silently would materialise a block that attaches fewer rows than the
 *  model asked for. Outside the links value nothing scans for URLs. */
export const ATTACH_BARE_URL_REGEX = /https?:\/\/[^\s<>()\[\]"']+/g;

/** The head of a bare-URL line. A `https://…` line also matches the param
 *  shape (`https` + `:` + the rest), so unknown-key accounting exempts every
 *  line that opens as a URL scheme — such a line is a link line wherever it
 *  appears, never an unknown key. */
export const ATTACH_URL_SCHEME_HEAD_REGEX = /^https?:\/\//i;

/** Defensive ceiling on one authored block body — past this point the body is
 *  not even split into lines. A block is a title and a link list; anything at
 *  this scale is malformed or hostile. */
export const ATTACH_MAX_INPUT_LENGTH = 20_000;

/** Defensive ceiling on one line of an authored block. A links line carrying
 *  the ceiling number of items sits well inside this. */
export const ATTACH_MAX_LINE_LENGTH = 4_000;

/** How many links one block may attach. The store caps nothing (ruled), but a
 *  SENTINEL is persisted content a model re-reads on every later read of that
 *  node, so the block itself is bounded. Links past this are dropped and
 *  counted, and the count rides the write's own tool result as a notice — the
 *  drop is reported, never silent. */
export const ATTACH_MAX_LINKS = 60;

/** Ceiling on the authored title, applied by truncation rather than refusal:
 *  a long title is a display problem, and refusing the write over one would
 *  throw away a correct attach. */
export const ATTACH_MAX_TITLE_LENGTH = 300;

/** Ceiling on one authored link's label, the title's posture exactly:
 *  truncated at the authoring boundary, never refused and never silently
 *  dropped. The bound is what makes a legal label's materialised field line
 *  (`  writer label: …`) provably survive the persisted grammar's 600-char
 *  line ceiling, which drops over-long lines WHOLE — without it a 700-char
 *  label would vanish from the block with nothing said. */
export const ATTACH_MAX_LABEL_LENGTH = 300;

// ─── The persisted block (our own) ──────────────────────────────────────────
//
// The info string moved to `grammar.ts` (`ATTACH_FENCE_INFO_STRING`) with the
// rest of the printed grammar, so the fence, the items and the ceilings are
// one contract in one file — the FE component that mounts on that fence reads
// its spelling from the same constant the renderer writes it from. The NAME is
// unchanged and ratified: `no-edit-data-block` (U11 §7.3, SPEC §2b).

/** The shortest fence marker the renderer emits. The body is now authored
 *  prose carrying captured values, so a value that is itself a backtick run
 *  can appear in it: `renderSentinelBlock` grows the marker past the longest
 *  all-backtick line rather than letting one close the block early. */
export const SENTINEL_FENCE_MARKER = '```';

/** The caption's two halves, around the capture datetime. RULED verbatim
 *  (§7.3), spacing included — `DO NOT MODIFY , either` carries its space
 *  before the comma exactly as ratified. */
export const SENTINEL_CAPTION_PREFIX = 'verified info for ';
export const SENTINEL_CAPTION_SUFFIX =
  ' (Auto pasted), DO NOT MODIFY , either leave or remove entirely';

/** Generic fence opener — ANY info string. The detector tracks these so a
 *  sentinel spelling quoted inside an unrelated fenced block is never claimed
 *  as a real sentinel. */
export const ANY_FENCE_OPEN_REGEX = /^[ \t]{0,3}(`{3,}|~{3,})[ \t]*(.*)$/;

/** Defensive ceiling on ONE sentinel's JSON payload. Above it the detector
 *  still reports the span (so the patch guard still protects it) but the
 *  envelope is not parsed — an unbounded JSON.parse on stored content is the
 *  one thing this module must never do. */
export const SENTINEL_MAX_PAYLOAD_LENGTH = 200_000;

// ─── The capture datetime ───────────────────────────────────────────────────

/** The caption's datetime format: `DD/MM/YYYY HH:mm`, in UTC.
 *
 *  The shape is the ruled seed's own (`16/08/2026 15:41`). UTC is the choice
 *  this build makes: the string is persisted forever inside content that
 *  outlives the session, is re-read by models and by the FE, and is compared
 *  by humans across timezones — a local-zone rendering would make the same
 *  capture read as two different times depending on who rendered it. The
 *  machine-readable `capturedAt` in the envelope stays full ISO-8601, so the
 *  caption is a reading of the data, never the data itself. */
export const SENTINEL_DATETIME_SEPARATOR = '/';
export const SENTINEL_DATETIME_TIME_SEPARATOR = ':';

/** The range caption's two joiners (ruled 2026-08-25, decision 1: a merged
 *  block's caption states its capture WINDOW). Same UTC day joins the two
 *  times tightly — `DD/MM/YYYY HH:mm–HH:mm` — and a cross-date range spells
 *  both ends in full around a spaced dash. Two instants in the same rendered
 *  minute collapse to the single-instant form, byte-identical to what every
 *  single-search block has always printed. */
export const SENTINEL_RANGE_TIME_SEPARATOR = '–';
export const SENTINEL_RANGE_DATETIME_SEPARATOR = ' – ';
